import { useMemo } from 'react'
import { Typography, useTheme } from '@mui/material'
import { BarDatum } from '@nivo/bar'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useTimeChartTicks from '@/hooks/useTimeChartTicks'
import { GroupOption } from '@/types/metrics'
import { ByTimeMetricReport } from '@/types/workflow-metric-reports'
import {
  formatter,
  getBottomMarginForCountChart,
  getLeftMarginForCountChart,
} from '@/utils/chart'
import BarChart from '@/components/charts/BarChart'
import LeftBorderedBox from '@/components/validation/LeftBorderedBox'
import { DateRange } from './ProjectMetricsDashboard'

type ByTimeAndStateMetricBarChartProps = {
  report?: ByTimeMetricReport
  groupBy: GroupOption
  selectedDateRange: DateRange
  filteredStateIds?: string[]
  height?: number
}

export default function ByTimeAndStateMetricBarChart({
  report,
  groupBy,
  selectedDateRange,
  filteredStateIds,
  height,
}: ByTimeAndStateMetricBarChartProps) {
  const theme = useTheme()
  const getThemeColor = useGetThemeColor()

  const { includeYear, axisBottom, needsToRotate, showingRange } =
    useTimeChartTicks({
      from: selectedDateRange.startDate,
      to: selectedDateRange.endDate,
      dataLength: report?.reports.length || 0,
      groupedBy: groupBy,
    })

  const keys =
    report?.reports[0]?.reports.map((report) => report.workflow_state__name) ||
    []

  const data =
    report?.reports.map((r) => {
      const rep: BarDatum = {
        date: r.date,
      }
      r.reports.forEach((report) => {
        if (
          filteredStateIds &&
          !filteredStateIds.includes(report.workflow_state_id)
        )
          return
        rep[report.workflow_state__name] = report.formatted_result
        rep[`${report.workflow_state__name}Color`] = getThemeColor(
          report.workflow_state__color,
        )
      })
      return rep
    }) || []

  // Since we are stacking the bars, we need to calculate the max y value among the sum of each day
  const maxYValue = useMemo(
    () =>
      Math.max(
        ...(report?.reports.map((d) =>
          d.reports.reduce((acc, report) => {
            if (
              filteredStateIds &&
              !filteredStateIds.includes(report.workflow_state_id)
            )
              return acc
            return acc + report.formatted_result
          }, 0),
        ) || [0]),
      ),
    [report, filteredStateIds],
  )

  return (
    <BarChart
      height={height || 250}
      data={data}
      indexBy="date"
      colors={(d) => {
        const color = d.data[`${d.id}Color`] as string
        return color || theme.palette.grey[200]
      }}
      keys={keys}
      labelSkipHeight={12}
      labelSkipWidth={20}
      padding={0.3}
      innerPadding={1}
      axisBottom={axisBottom}
      axisLeft={{
        format: (e) => (Math.floor(e) === e ? e : ''),
      }}
      margin={{
        bottom: getBottomMarginForCountChart(
          needsToRotate,
          includeYear,
          showingRange,
        ),
        left: getLeftMarginForCountChart(maxYValue),
        right: 20,
      }}
      labelTextColor={(d) => {
        const color = d.data.data[`${d.data.id}Color`] as string
        return theme.palette.getContrastText(color)
      }}
      tooltipContent={(props) => (
        <LeftBorderedBox color={props.color} sx={{ p: 1, pl: 1.75 }}>
          <Typography variant="body2" component="div" noWrap>
            {props.id} -{' '}
            {formatter(
              props.data.date as string,
              groupBy,
              includeYear,
              selectedDateRange.endDate,
            )}
            : <b>{props.formattedValue}</b>
          </Typography>
        </LeftBorderedBox>
      )}
    />
  )
}
